"use client";

import type React from "react";
import { useState, useRef, useEffect } from "react";
import { EditIcon } from "@chakra-ui/icons";
import { Flex, Heading, Input, IconButton, useToast } from "@chakra-ui/react";
import { agentService } from "../../services/agentService.ts";
import { useAppTheme } from "../../ThemeContext.tsx";

interface EditableAgentNameProps {
  agentId: string;
  initialName: string;
}

export const EditableAgentName: React.FC<EditableAgentNameProps> = ({
  agentId,
  initialName,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(initialName);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (name.trim() === "") {
      setName(initialName);
      setIsEditing(false);
      return;
    }

    try {
      await agentService.update(agentId, { agent_name: name });
      setIsEditing(false);
      toast({
        title: "Agent name updated",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error updating agent name:", error);
      toast({
        title: "Error updating agent name",
        status: "error",
        duration: 3000,
      });
      setName(initialName);
      setIsEditing(false);
    }
  };

  return (
    <Flex align="left">
      {isEditing ? (
        <Input
          ref={inputRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSave();
            } else if (e.key === "Escape") {
              setName(initialName);
              setIsEditing(false);
            }
          }}
          size="lg"
          width="auto"
          height="auto"
          fontWeight="medium"
          _focus={{ borderColor: "transparent" }}
          borderColor="transparent"
          variant="filled"
          outline="none"
        />
      ) : (
        <>
          <Heading size="md" fontWeight="medium">
            {name}
          </Heading>
          <IconButton
            aria-label="Edit agent name"
            icon={<EditIcon />}
            size="xs"
            variant="ghost"
            ml={1}
            onClick={handleEdit}
            color={isDark ? "gray.400" : "gray.500"}
            _hover={{
              color: isDark ? "white" : "gray.800",
              bg: isDark ? "gray.700" : "gray.200",
            }}
          />
        </>
      )}
    </Flex>
  );
};
